import React, { useMemo } from 'react';
import type { PublicTrustSnapshot } from '../../../types/publicTrust.ts';
import { usePhoneViewport } from '../../../hooks/usePhoneViewport.ts';
import {
  buildTrustAvatarLead,
  buildTrustAvatarSlots,
  buildTrustCardHeadline,
  buildTrustCardMobileLine,
  buildTrustCardSubhead,
  getTrustAvatarOverflowCount,
  getTrustVisibleAvatarSlotCount,
  shouldShowTrustAvatars,
} from '../../../utils/publicTrust/publicTrustCopy.ts';
import TrustAvatarSlotInteractive from './TrustAvatarSlot.tsx';
import TrustOverflowBadge from './TrustOverflowBadge.tsx';
import styles from './PublicTrustSnapshotCard.module.scss';

type PublicTrustSnapshotCardProps = {
  snapshot: PublicTrustSnapshot | null;
  isLoading?: boolean;
};

function getPopoverAlign(index: number, total: number): 'start' | 'center' | 'end' {
  if (index === 0) return 'end';
  if (index >= total - 1) return 'end';
  return 'center';
}

const PublicTrustSnapshotCard: React.FC<PublicTrustSnapshotCardProps> = ({
  snapshot,
  isLoading = false,
}) => {
  const isPhoneViewport = usePhoneViewport();

  const cardModel = useMemo(() => {
    if (!snapshot) return null;

    const { stats, meta, activity } = snapshot;
    const showAvatars = shouldShowTrustAvatars(meta);
    const visibleAvatarSlots = getTrustVisibleAvatarSlotCount(isPhoneViewport);
    const avatarSlots = showAvatars ? buildTrustAvatarSlots(activity, visibleAvatarSlots) : [];
    const overflowCount = getTrustAvatarOverflowCount(stats, meta, avatarSlots.length);

    return {
      headline: buildTrustCardHeadline(stats, meta),
      subhead: buildTrustCardSubhead(stats, meta),
      mobileLine: buildTrustCardMobileLine(stats, meta),
      avatarLead: buildTrustAvatarLead(stats, meta, activity.length),
      avatarSlots,
      overflowCount,
      showAvatars: avatarSlots.length > 0,
    };
  }, [snapshot, isPhoneViewport]);

  if (isLoading && !snapshot) {
    return (
      <div className={`${styles.cardSkeleton} ${isPhoneViewport ? styles.cardPhone : ''}`} aria-busy="true" aria-label="Cargando red de profesionales">
        <div className={styles.cardMainRow}>
          <div className={styles.copySkeleton}>
            <span className={styles.lineSkeleton} />
            <span className={styles.lineSkeletonShort} />
          </div>
          <div className={styles.avatarAside}>
            <span className={styles.avatarLeadSkeleton} />
            <div className={styles.avatarRow}>
              <span className={styles.avatarSkeleton} />
              <span className={styles.avatarSkeleton} />
              <span className={styles.avatarSkeleton} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!cardModel) return null;

  return (
    <article
      className={`${styles.card} ${cardModel.showAvatars ? '' : styles.cardNoAvatars} ${isPhoneViewport ? styles.cardPhone : ''}`}
      aria-label="Red de profesionales en Edify"
    >
      <div className={styles.cardMainRow}>
        <div className={styles.copyBlock}>
          {isPhoneViewport ? (
            <p className={styles.headline}>{cardModel.mobileLine}</p>
          ) : (
            <>
              <p className={styles.headline}>{cardModel.headline}</p>
              <p className={styles.subhead}>{cardModel.subhead}</p>
            </>
          )}
        </div>

        {cardModel.showAvatars ? (
          <div className={styles.avatarAside}>
            {cardModel.avatarLead ? (
              <p className={styles.avatarLead}>{cardModel.avatarLead}</p>
            ) : null}
            <div className={styles.avatarRow}>
              <div className={styles.avatarStack}>
                {cardModel.avatarSlots.map((slot, index) => (
                  <TrustAvatarSlotInteractive
                    key={slot.key}
                    slot={slot}
                    popoverAlign={getPopoverAlign(index, cardModel.avatarSlots.length)}
                  />
                ))}
              </div>
              {cardModel.overflowCount > 0 ? (
                <TrustOverflowBadge count={cardModel.overflowCount} />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default PublicTrustSnapshotCard;
